import React, { useState, useRef, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Center,
  Image,
  Text,
  Box,
  VStack,
  Icon,
  HStack,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  SliderMark,
  Flex,
} from "@chakra-ui/react";
// ✅ FaEye 아이콘을 투명도 슬라이더에 사용하기 위해 import 합니다.
import { FaVolumeUp, FaImage, FaPlay, FaPause, FaStop, FaEye } from "react-icons/fa";

import sampleAudio from "assets/audio/sample.mp3";
// 배경으로 사용할 샘플 이미지를 import 합니다.
import sampleBackgroundImage from "assets/img/sample-image.jpg";  
import sampleWatermarkImage from "assets/img/watermark.jpg";


// PreviewModal에 전달할 데이터의 타입을 정의합니다.
interface PreviewData {
  previewType: "visible-text" | "visible-file" | "audible";
  watermarkText?: string;
  opacity?: number;
  volume?: number;
}

// PreviewModal이 받을 Props 타입 정의
interface PreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  PreviewData: {
    previewType: "visible-text" | "visible-file" | "audible";
    wmText?: string; // visible-text 타입에서 사용할 워터마크 텍스트
    templateType?: number; // visible-text 타입에서 사용할 템플릿 타입 (0-4)
    opacity?: number; // 워터마크 투명도 (0-100)
    volume?: number; // audible 타입에서 사용할 볼륨 (0-100)
  };
}

// ✅ WatermarkOverlay가 받을 Props 타입
interface WatermarkOverlayProps {
  templateType: number;
  text: string;
  opacity: number;
}

type PlaybackStatus = "playing" | "paused" | "stopped";

const PreviewModal = ({
  isOpen,
  onClose,
  PreviewData: {
    previewType,
    wmText = "Watermark", // visible-text 타입에서 사용할 기본 워터마크 텍스트
    templateType = 1, // visible-text 타입에서 사용할 기본 템플릿 타입 (0: 중앙)
    opacity = 100, // 워터마크 투명도 (기본값: 100)
    volume = 100, // audible 타입에서 사용할 볼륨 (기본값: 100)
  },
}: PreviewModalProps): React.ReactElement => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playbackStatus, setPlaybackStatus] = useState<PlaybackStatus>("stopped");
  const [previewVolume, setPreviewVolume] = useState(volume);
  
  // ✅ 프리뷰 모달 내에서 사용할 로컬 투명도 상태를 추가합니다.
  const [previewOpacity, setPreviewOpacity] = useState(opacity);

  // 오디오 제어 함수들 (변경 없음)
  const handlePlay = () => { if (audioRef.current) { audioRef.current.play(); setPlaybackStatus("playing"); } };
  const handlePause = () => { if (audioRef.current) { audioRef.current.pause(); setPlaybackStatus("paused"); } };
  const handleStop = () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; setPlaybackStatus("stopped"); } };

  /**
 * templateType에 따라 4가지 다른 워터마크 오버레이를 렌더링하는 컴포넌트
 */
const WatermarkOverlay = ({ templateType, text, opacity }: WatermarkOverlayProps) => {
  // 워터마크 텍스트에 일관된 스타일 적용
  const watermarkTextStyle = {
    fontSize: "3xl",
    fontWeight: "bold",
    color: "rgb(0, 0, 0)", // 검은색 텍스트
    // textShadow: "0px 1px 4px black",   // 검은색 그림자
    // whiteSpace: "nowrap", // 텍스트가 줄바꿈되지 않도록 설정
    p: 2, // 텍스트 간 간격 확보를 위한 패딩
  };

  const renderContent = () => {
    switch (templateType) {
      // Case 1: 대각선 반복
      case 2:
        return (
          // 1. 넘침을 감출 컨테이너
          <Box position="absolute" w="full" h="full" overflow="hidden">
            {/* 2. 회전 및 텍스트를 담을 더 큰 컨테이너 */}
            <Flex
              w="200%" // 부모보다 훨씬 크게 만들어 회전 시 빈 공간이 없도록 함
              h="200%"
              position="absolute"
              top="-50%"
              left="-50%"
              transform="rotate(-45deg)" // 45도 회전
              wrap="wrap"
              gap={8} // 텍스트 간격
              alignItems="center"
              justifyContent="center"
            >
              {Array.from({ length: 200 }).map((_, i) => (
                <Text key={i} {...watermarkTextStyle}>{text}</Text>
              ))}
            </Flex>
          </Box>
        );

      // Case 2: 수평 반복
      case 3:
        return (
          <Flex
            position="absolute"
            w="full"
            h="full"
            wrap="wrap"
            gap={10} // 텍스트 간격
            alignItems="center"
            justifyContent="center"
            overflow="hidden"
          >
            {Array.from({ length: 50 }).map((_, i) => (
              <Text key={i} {...watermarkTextStyle}>{text}</Text>
            ))}
          </Flex>
        );

      // Case 4: 우측 상단
      case 4:
        return (
          <Box position="absolute" top={4} right={4}>
            <Text {...watermarkTextStyle}>{text}</Text>
          </Box>
        );

      // Case 0: 중앙 (기본값)
      case 1:
      default:
        return (
          <Center position="absolute" w="full" h="full" p={4}>
            <Text {...watermarkTextStyle} fontSize="4xl">{text}</Text>
          </Center>
        );
    }
  };
  
  // 전체 오버레이에 투명도 적용
  return <Box w="full" h="full" opacity={opacity / 100}>{renderContent()}</Box>
};

  // 모달이 열리거나 부모의 props가 바뀔 때 로컬 상태들을 초기화합니다.
  useEffect(() => {
    if (isOpen) {
      setPreviewVolume(volume);
      setPreviewOpacity(opacity); // 투명도 상태도 함께 초기화
    }
  }, [isOpen, volume, opacity]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = previewVolume / 100;
    }
  }, [previewVolume]);

  useEffect(() => {
    if (!isOpen) {
      handleStop();
    }
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>SAMPLE</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <audio ref={audioRef} src={sampleAudio} loop />
          <Center minH="200px">
            {/* ====================================================== */}
            {/* ✅ visible-text 미리보기 (수정됨) */}
            {/* ====================================================== */}
            {previewType === "visible-text" && (
              <VStack spacing={5} w="100%">
                <Text fontWeight="bold">워터마크 투명도: {previewOpacity}%</Text>
                
                 <Box
                  position="relative"
                  w="full"
                  h="250px"
                  bgImage={`url(${sampleBackgroundImage})`}
                  // ... (기타 Box 스타일은 동일)
                >
                  {/* ✅ prop으로 받은 텍스트와 템플릿 타입을 WatermarkOverlay로 전달 */}
                  <WatermarkOverlay
                    templateType={templateType}
                    text={wmText}
                    opacity={previewOpacity}
                  />
                </Box>
                
                {/* 실시간 투명도 조절 슬라이더 */}
                <HStack w="90%">
                  <Icon as={FaEye} color="gray.500" />
                  <Slider
                    aria-label="opacity-slider"
                    value={previewOpacity}
                    onChange={(val) => setPreviewOpacity(val)}
                  >
                    {/* 현재 슬라이더 값 (백분율)을 표시하는 SliderMark */}
                    {/* thumb의 위치에 따라 동적으로 값 표시 */}
                    <SliderMark
                      value={previewOpacity ?? 100} // 현재 값
                      textAlign="center"
                      bg="blue.500"
                      color="white"
                      mt="-8" // 슬라이더 위로 표시
                      ml="-5" // 가운데 정렬을 위해 왼쪽 마진 조정
                      w="10" // 너비
                      borderRadius="md"
                      visibility={previewOpacity === undefined ? "hidden" : "visible"} // 초기값 undefined일 때 숨김
                    >
                      {previewOpacity ?? 0}% {/* 값 표시 */}
                    </SliderMark>

                    <SliderTrack><SliderFilledTrack bg="blue.500" /></SliderTrack>
                    <SliderThumb
                      borderColor="black"
                      borderWidth="1px"
                      boxShadow="outline"
                    />
                  </Slider>
                </HStack>
              </VStack>
            )}

            {/* ====================================================== */}
            {/* ✅ visible-file 미리보기 (수정됨) */}
            {/* ====================================================== */}
            {previewType === "visible-file" && (
              <VStack spacing={5} w="100%">
                <Text fontWeight="bold">워터마크 투명도: {previewOpacity}%</Text>
                
                {/* 배경 이미지 Box */}
                <Box
                  position="relative"
                  w="full"
                  h="250px"
                  bgImage={`url(${sampleBackgroundImage})`}
                  bgSize="cover"
                  bgPosition="center"
                  borderRadius="md"
                  border="1px solid"
                  borderColor="gray.200"
                >
                  {/* prop으로 받은 이미지를 워터마크로 표시하는 오버레이 */}
                  <Center
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    opacity={previewOpacity / 100}
                  >
                    {sampleWatermarkImage ? (
                       <Image src={sampleWatermarkImage} alt="Watermark Image" maxW="80%" maxH="80%" />
                    ) : (
                       <Text color="red.500">이미지 없음</Text>
                    )}
                  </Center>
                </Box>
                
                {/* 실시간 투명도 조절 슬라이더 */}
                <HStack w="90%">
                  <Icon as={FaEye} color="gray.500" />
                  <Slider
                    aria-label="opacity-slider"
                    value={previewOpacity}
                    onChange={(val) => setPreviewOpacity(val)}
                  >
                    {/* 현재 슬라이더 값 (백분율)을 표시하는 SliderMark */}
                    {/* thumb의 위치에 따라 동적으로 값 표시 */}
                    <SliderMark
                      value={previewOpacity ?? 100} // 현재 값
                      textAlign="center"
                      bg="blue.500"
                      color="white"
                      mt="-8" // 슬라이더 위로 표시
                      ml="-5" // 가운데 정렬을 위해 왼쪽 마진 조정
                      w="10" // 너비
                      borderRadius="md"
                      visibility={previewOpacity === undefined ? "hidden" : "visible"} // 초기값 undefined일 때 숨김
                    >
                      {previewOpacity ?? 0}% {/* 값 표시 */}
                    </SliderMark>

                    <SliderTrack><SliderFilledTrack bg="blue.500" /></SliderTrack>
                    <SliderThumb
                      borderColor="black"
                      borderWidth="1px"
                      boxShadow="outline"
                    />
                  </Slider>
                </HStack>
              </VStack>
            )}

            {/* ====================================================== */}
            {/* audible 미리보기 부분 (변경 없음) */}
            {/* ====================================================== */}
            {previewType === "audible" && (
              <VStack spacing={5} w="100%">
                <Text fontWeight="bold">워터마크 볼륨: {previewVolume}%</Text>
                <HStack w="80%">
                  <Icon as={FaVolumeUp} color="gray.500" />
                  <Slider
                    aria-label="volume-slider"
                    value={previewVolume}
                    onChange={(val) => setPreviewVolume(val)}
                  >
                    {/* 현재 슬라이더 값 (백분율)을 표시하는 SliderMark */}
                    {/* thumb의 위치에 따라 동적으로 값 표시 */}
                    <SliderMark
                      value={previewVolume ?? 100} // 현재 값
                      textAlign="center"
                      bg="blue.500"
                      color="white"
                      mt="-8" // 슬라이더 위로 표시
                      ml="-5" // 가운데 정렬을 위해 왼쪽 마진 조정
                      w="10" // 너비
                      borderRadius="md"
                      visibility={previewVolume === undefined ? "hidden" : "visible"} // 초기값 undefined일 때 숨김
                    >
                      {previewVolume ?? 0}% {/* 값 표시 */}
                    </SliderMark>

                    <SliderTrack><SliderFilledTrack bg="blue.500" /></SliderTrack>
                    <SliderThumb
                      borderColor="black"
                      borderWidth="1px"
                      boxShadow="outline"
                    />
                  </Slider>
                </HStack>
                <HStack spacing={4} mt={2}>
                  <Button onClick={handlePlay} isDisabled={playbackStatus === "playing"} colorScheme="teal" leftIcon={<FaPlay />}>재생</Button>
                  <Button onClick={handlePause} isDisabled={playbackStatus !== "playing"} colorScheme="orange" leftIcon={<FaPause />}>일시정지</Button>
                  <Button onClick={handleStop} isDisabled={playbackStatus === "stopped"} colorScheme="red" leftIcon={<FaStop />}>정지</Button>
                </HStack>
              </VStack>
            )}
          </Center>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="blue" onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PreviewModal;